import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatRupiah } from '../utils/helpers';
import { toast } from 'sonner';
import { Search, Download, ArrowRightLeft, Package, X } from 'lucide-react';

const Inventori = () => {
  const { inventori, cabang, fetchInventori } = useApp();
  const { canManageTransactions, hasGlobalAccess } = useAuth();
  const [isMigrasiOpen, setIsMigrasiOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState('');
  const [exportFormat, setExportFormat] = useState('pdf');
  const [filterCabang, setFilterCabang] = useState('all');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [migrasiData, setMigrasiData] = useState({
    inventori_id: '',
    cabang_tujuan_id: null,
    jumlah: 1
  });

  const handleFilter = (cabangId, searchTerm) => {
    const params = {};
    setCurrentPage(1);
    if (cabangId && cabangId !== 'all') params.cabang_id = cabangId;
    if (searchTerm) params.search = searchTerm;
    fetchInventori(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilter(filterCabang, search);
  };

  const handleCabangFilter = (value) => {
    setFilterCabang(value);
    handleFilter(value, search);
  };

  const clearFilters = () => {
    setSearch('');
    setFilterCabang('all');
    setCurrentPage(1);
    fetchInventori();
  };

  const openMigrasiDialog = (item) => {
    if (!canManageTransactions) {
      toast.error('Role ini hanya memiliki akses baca pada inventori');
      return;
    }
    setSelectedItem(item);
    setMigrasiData({
      inventori_id: item.id,
      cabang_tujuan_id: null,
      jumlah: 1
    });
    setIsMigrasiOpen(true);
  };

  const handleMigrasi = async () => {
    if (!canManageTransactions) {
      toast.error('Akses ditolak untuk migrasi inventori');
      return;
    }
    try {
      if (migrasiData.jumlah < 1 || migrasiData.jumlah > selectedItem.jumlah) {
        toast.error('Jumlah tidak valid');
        return;
      }

      const targetCabang = migrasiData.cabang_tujuan_id === 'global' ? null : migrasiData.cabang_tujuan_id;
      const sourceItems = selectedItem?._sourceItems?.length ? selectedItem._sourceItems : (selectedItem ? [selectedItem] : []);
      if (sourceItems.length === 0) {
        toast.error('Item tidak valid');
        return;
      }

      let remaining = migrasiData.jumlah;

      for (const item of sourceItems) {
        if (remaining <= 0) break;
        const qty = Math.min(remaining, item.jumlah);
        if (qty <= 0) continue;

        await api.migrasiInventori({
          inventori_id: item.id,
          cabang_tujuan_id: targetCabang,
          jumlah: qty
        });

        remaining -= qty;
      }

      if (remaining > 0) {
        toast.error('Stok tidak cukup');
        return;
      }

      toast.success('Migrasi inventori berhasil');
      setIsMigrasiOpen(false);
      fetchInventori();
    } catch (error) {
      toast.error('Gagal melakukan migrasi');
      console.error(error);
    }
  };

  const getCabangName = (cabangId) => {
    if (!cabangId) return 'Global';
    const found = cabang.find(c => c.id === cabangId);
    return found ? found.nama : 'Global';
  };

  const normalizeName = (name) => (name || '').trim().toLowerCase();

  const groupedInventori = useMemo(() => {
    const map = new Map();

    for (const item of inventori) {
      const cabangKey = item.cabang_id || 'global';
      const nameKey = normalizeName(item.nama_barang);
      const key = `${cabangKey}::${nameKey}`;

      let entry = map.get(key);
      if (!entry) {
        entry = {
          ...item,
          jumlah: 0,
          harga_total: 0,
          _groupKey: key,
          _sourceItems: [],
          _keteranganSet: new Set(),
        };
        map.set(key, entry);
      }

      entry.jumlah += item.jumlah || 0;
      entry.harga_total += item.harga_total || 0;
      entry._sourceItems.push(item);
      if (item.keterangan) {
        entry._keteranganSet.add(item.keterangan);
      }
    }

    return Array.from(map.values())
      .map((entry) => {
      const totalQty = entry.jumlah || 0;
      const hargaSatuan = totalQty > 0 ? entry.harga_total / totalQty : 0;
      const notes = Array.from(entry._keteranganSet);
      let keterangan = '-';
      if (notes.length === 1) keterangan = notes[0];
      if (notes.length > 1) keterangan = `${notes[0]} (+${notes.length - 1})`;

      const { _keteranganSet, ...rest } = entry;
      return {
        ...rest,
        harga_satuan: hargaSatuan,
        keterangan,
      };
      })
      .sort((a, b) => (a.nama_barang || '').localeCompare(b.nama_barang || '', 'id', { sensitivity: 'base' }));
  }, [inventori]);

  // Group inventory by cabang
  const inventoriByCabang = groupedInventori.reduce((acc, item) => {
    const key = item.cabang_id || 'global';
    if (!acc[key]) {
      acc[key] = {
        nama: getCabangName(item.cabang_id),
        items: [],
        totalQty: 0,
        totalNilai: 0
      };
    }
    acc[key].items.push(item);
    acc[key].totalQty += item.jumlah;
    acc[key].totalNilai += item.harga_total;
    return acc;
  }, {});

  const totalQty = groupedInventori.reduce((sum, i) => sum + i.jumlah, 0);
  const totalNilai = groupedInventori.reduce((sum, i) => sum + i.harga_total, 0);

  const totalPages = Math.max(1, Math.ceil(groupedInventori.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pagedInventori = groupedInventori.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  return (
    <div className="space-y-6" data-testid="inventori-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Inventori</h1>
          <p className="text-muted-foreground text-sm mt-1">Kelola stok barang global dan per cabang</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={exportFormat} onValueChange={setExportFormat}>
            <SelectTrigger className="w-[90px]" data-testid="export-format-inventori">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf">PDF</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="xlsx">Excel</SelectItem>
            </SelectContent>
          </Select>
          <a href={api.exportInventori(filterCabang !== 'all' ? filterCabang : null, exportFormat)} target="_blank" rel="noreferrer">
            <Button variant="secondary" size="sm" data-testid="export-inventori-btn">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </a>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Cari nama barang..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-inventori"
                />
              </div>
              <Button type="submit" variant="secondary" size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </form>
            <Select value={filterCabang} onValueChange={handleCabangFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-cabang-inventori">
                <SelectValue placeholder="Semua Cabang" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Cabang</SelectItem>
                {hasGlobalAccess && <SelectItem value="global">Global</SelectItem>}
                {cabang.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nama}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(search || filterCabang !== 'all') && (
              <Button variant="ghost" size="icon" onClick={clearFilters}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase text-blue-700">Total Item</p>
            <p className="text-2xl font-black text-blue-600">{totalQty}</p>
          </CardContent>
        </Card>
        <Card className="bg-violet-50 border-violet-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase text-violet-700">Nilai Inventori</p>
            <p className="text-2xl font-black text-violet-600">{formatRupiah(totalNilai)}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-50 border-zinc-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase text-zinc-700">Jenis Barang</p>
            <p className="text-2xl font-black text-zinc-600">{groupedInventori.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-xs font-semibold uppercase text-amber-700">Lokasi Cabang</p>
            <p className="text-2xl font-black text-amber-600">{Object.keys(inventoriByCabang).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Inventory by Branch Summary */}
      {Object.keys(inventoriByCabang).length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(inventoriByCabang).map(([key, data]) => (
            <Card key={key} className="bg-white border border-border/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <Package className="h-4 w-4 text-blue-500" />
                  {data.nama}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{data.items.length} jenis barang</span>
                  <span className="font-mono font-bold">{data.totalQty} unit</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-muted-foreground">Nilai</span>
                  <span className="font-mono font-bold text-blue-600">{formatRupiah(data.totalNilai)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="bg-white border border-border/60">
        <CardContent className="p-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Tampilkan</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(parseInt(v, 10)); setCurrentPage(1); }}>
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">data</span>
            </div>
            <div className="text-sm text-muted-foreground">
              {groupedInventori.length === 0 ? 0 : startIndex + 1} - {Math.min(startIndex + pageSize, groupedInventori.length)} dari {groupedInventori.length}
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-[50px]">No</TableHead>
                  <TableHead>Nama Barang</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Harga Satuan</TableHead>
                  <TableHead className="text-right">Total Nilai</TableHead>
                  <TableHead>Lokasi</TableHead>
                  <TableHead>Keterangan</TableHead>
                  <TableHead className="w-[100px] text-center">Migrasi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedInventori.length > 0 ? pagedInventori.map((item, index) => (
                  <TableRow key={item._groupKey || item.id} className="hover:bg-zinc-50/50">
                    <TableCell className="font-mono text-muted-foreground">{startIndex + index + 1}</TableCell>
                    <TableCell className="font-medium">{item.nama_barang}</TableCell>
                    <TableCell className="text-right font-mono font-bold">{item.jumlah}</TableCell>
                    <TableCell className="text-right font-mono">{formatRupiah(item.harga_satuan)}</TableCell>
                    <TableCell className="text-right font-mono font-bold text-blue-600">
                      {formatRupiah(item.harga_total)}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.cabang_id ? 'bg-violet-100 text-violet-700' : 'bg-zinc-100 text-zinc-700'
                      }`}>
                        {getCabangName(item.cabang_id)}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">
                      {item.keterangan || '-'}
                    </TableCell>
                    <TableCell>
                      {canManageTransactions ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openMigrasiDialog(item)}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`migrasi-inventori-${item.id}`}
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                      ) : (
                        <div className="text-center text-xs text-muted-foreground">Read only</div>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Belum ada data inventori
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end gap-2 p-4 border-t">
            <span className="text-sm text-muted-foreground">Hal {currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Next
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Migrasi Dialog */}
      <Dialog open={isMigrasiOpen} onOpenChange={setIsMigrasiOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Migrasi Inventori</DialogTitle>
            <DialogDescription className="sr-only">
              Form untuk memindahkan inventori ke cabang lain
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-zinc-50 rounded-sm">
                <p className="font-bold">{selectedItem.nama_barang}</p>
                <p className="text-sm text-muted-foreground">
                  Stok tersedia: {selectedItem.jumlah} unit
                </p>
                <p className="text-sm text-muted-foreground">
                  Lokasi saat ini: {getCabangName(selectedItem.cabang_id)}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Jumlah yang dipindahkan</Label>
                <Input
                  type="number"
                  value={migrasiData.jumlah}
                  onChange={(e) => setMigrasiData({ ...migrasiData, jumlah: parseInt(e.target.value) || 1 })}
                  min="1"
                  max={selectedItem.jumlah}
                  data-testid="input-jumlah-migrasi"
                />
              </div>
              <div className="space-y-2">
                <Label>Cabang Tujuan</Label>
                <Select 
                  value={migrasiData.cabang_tujuan_id || 'global'} 
                  onValueChange={(v) => setMigrasiData({ ...migrasiData, cabang_tujuan_id: v === 'global' ? null : v })}
                >
                  <SelectTrigger data-testid="select-cabang-tujuan">
                    <SelectValue placeholder="Pilih Cabang Tujuan" />
                  </SelectTrigger>
                  <SelectContent>
                    {hasGlobalAccess && <SelectItem value="global">Global</SelectItem>}
                    {cabang.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nama}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsMigrasiOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleMigrasi} data-testid="submit-migrasi-btn">
              Pindahkan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventori;
